import { type ReactNode } from 'react'

export type BadgeColor = 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'secondary'

const subtleClasses: Record<BadgeColor, string> = {
  primary: 'bg-primary-100 text-link',
  success: 'bg-success-100 text-positive',
  danger: 'bg-danger-100 text-negative',
  warning: 'bg-warning-100 text-notice',
  info: 'bg-info-100 text-informative',
  secondary: 'bg-surface-strong text-body',
}

const solidClasses: Record<BadgeColor, string> = {
  primary: 'bg-primary-500 text-on-dark',
  success: 'bg-success-700 text-on-dark',
  danger: 'bg-danger-600 text-on-dark',
  // I toni 500/600 con testo bianco non raggiungono la soglia WCAG AA per testo
  // piccolo (serve 4.5:1): i toni 700 sono abbastanza scuri da reggere il
  // bianco come le altre varianti, restando comunque distinguibile come colore.
  warning: 'bg-warning-700 text-on-dark',
  info: 'bg-info-700 text-on-dark',
  // secondary-500 con testo bianco era anch'esso sotto la soglia WCAG AA (~3.06:1,
  // stesso problema di warning/info sopra, sfuggito alla verifica precedente).
  secondary: 'bg-[var(--color-inverse-surface)] text-on-dark',
}

const dotClasses: Record<BadgeColor, string> = {
  primary: 'bg-primary-600',
  success: 'bg-success-600',
  danger: 'bg-danger-600',
  warning: 'bg-warning-600',
  info: 'bg-info-600',
  secondary: 'bg-secondary-600',
}

interface StatusBadgeProps {
  color: BadgeColor
  label: string
  icon?: ReactNode
  pill?: boolean
  solid?: boolean
  /** Pallino colorato prima del testo: rende lo stato leggibile anche senza fare affidamento solo sul colore. */
  dot?: boolean
  className?: string
}

/** Badge standard del design system: sostituisce sia bg-{color} pieno sia bg-{color}-subtle text-{color} di Bootstrap. */
export function StatusBadge({ color, label, icon, pill, solid, dot, className = '' }: StatusBadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium whitespace-nowrap ${pill ? 'rounded-full' : 'rounded'} ${solid ? solidClasses[color] : subtleClasses[color]} ${className}`}
    >
      {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${solid ? 'bg-canvas/70' : dotClasses[color]}`} />}
      {icon}
      {label}
    </span>
  )
}
